import _initializerDefineProperty from "@babel/runtime/helpers/initializerDefineProperty";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
import _initializerWarningHelper from "@babel/runtime/helpers/initializerWarningHelper";
var _dec, _dec2, _dec3, _class, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5;
import { action, observable } from 'mobx';
import moment from 'moment';
import api from 'core/util/api';
import { Model } from 'core/model/Model';
import { DeviceTrapKeys } from '@kentik/ui-shared/device/constants';
import { PagedCollection } from 'core/model/PagedCollection';
/**
 * Removes irrelevant fields from filter object (filter fields which have no associated filter value)
 */
const cleanFilters = (filters = {}) => Object.fromEntries(Object.entries(filters).filter(([, values]) => !!values?.length));
const TRAP_WINDOW_MS = 3600 * 1000;
const MINUTE_MS = 60 * 1000;
export let DeviceTrapsCollection = (_dec = observable.ref, _dec2 = observable.ref, _dec3 = observable.ref, _class = class DeviceTrapsCollection extends PagedCollection {
  get model() {
    return Model;
  }
  constructor(device) {
    super();
    _initializerDefineProperty(this, "device", _descriptor, this);
    _initializerDefineProperty(this, "filters", _descriptor2, this);
    _initializerDefineProperty(this, "searchTerm", _descriptor3, this);
    /** start/end in milliseconds */
    _initializerDefineProperty(this, "timeRange", _descriptor4, this);
    _initializerDefineProperty(this, "queryReady", _descriptor5, this);
    this.device = device;
    this.serverSortableFields = [DeviceTrapKeys.timestamp, DeviceTrapKeys.trap_type];
    this.setPageState({
      perPage: 20
    });
    this.requestStatus = 'awaitingRequest';
  }
  get url() {
    return `/api/ui/devices/${this.device.id}/traps/query`;
  }
  get fetchMethod() {
    return 'post';
  }
  get jsonRoot() {
    return 'data';
  }
  get queuedFetchKey() {
    return `DeviceTrapsCollection.${this.device.id}`;
  }
  get defaultSortState() {
    return {
      field: DeviceTrapKeys.timestamp,
      direction: 'desc'
    };
  }
  get sortParam() {
    const {
      field,
      direction
    } = this.sortState;
    return {
      field,
      direction
    };
  }
  setFilters(filters) {
    this.filters = filters;
  }
  setSearchTerm(searchTerm) {
    this.searchTerm = searchTerm;
  }

  /**
   * @param timeRange start/end in milliseconds
   */
  setTimeRange(timeRange) {
    this.timeRange = timeRange;
  }

  /**
   * Moves the time range to the hour containing the device's most recent trap. Keeps the current range when
   * there is no trap or the lookup fails.
   * @returns whether the range was moved
   */
  async jumpToLatestTrap() {
    try {
      const {
        lastSeen
      } = await api.get(`/api/ui/devices/${this.device.id}/traps/last-seen`);
      const latest = Number(lastSeen) * 1000;
      if (!Number.isFinite(latest) || latest <= 0) {
        return false;
      }
      // window end is exclusive, so round up to the next minute to include the trap itself
      const endDate = (Math.floor(latest / MINUTE_MS) + 1) * MINUTE_MS;
      this.setTimeRange({
        startDate: endDate - TRAP_WINDOW_MS,
        endDate
      });
      return true;
    } catch {
      // keep the current range
      return false;
    }
  }

  /**
   * Enables querying. Fetches are skipped until the initial query state (e.g. saved search term) has been applied.
   */
  setQueryReady(queryReady) {
    this.queryReady = queryReady;
  }

  /**
   * Request body for the traps query, built from the collection's own filter, window, search, sort and page state.
   */
  get queryData() {
    const {
      field,
      direction
    } = this.sortParam;
    return {
      filters: cleanFilters(this.filters),
      window: {
        startDate: this.timeRange.startDate / 1000,
        endDate: this.timeRange.endDate / 1000
      },
      pagination: {
        limit: this.pageState.perPage,
        offset: this.pageState.offset
      },
      search: this.searchTerm,
      sort: {
        field,
        direction
      }
    };
  }

  /**
   * Fetches the current page using the collection's query state via the queued `pagedFetch`.
   */
  fetchPage({
    perPage
  } = {}) {
    if (!this.queryReady) {
      return Promise.resolve();
    }
    if (perPage !== undefined) {
      this.setPageState({
        perPage
      });
    }
    return this.pagedFetch({
      data: this.queryData
    });
  }

  /**
   * Server sort (and any change to filters/search/window) returns to the first page and re-queries with the
   * current state, rather than the base implementation's un-queued `loadMoreItems`.
   */
  serverSortFn() {
    this.resetPageState();
    return this.fetchPage();
  }
  processData(response) {
    const result = super.processData(response);
    this.setPageState({
      pageCountLoaded: true
    });
    return result;
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "device", [_dec], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "filters", [_dec2], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return {};
  }
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "searchTerm", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return '';
  }
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "timeRange", [_dec3], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return {
      startDate: moment().subtract(1, 'hours').seconds(0).unix() * 1000,
      endDate: moment().seconds(0).unix() * 1000
    };
  }
}), _descriptor5 = _applyDecoratedDescriptor(_class.prototype, "queryReady", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _applyDecoratedDescriptor(_class.prototype, "setFilters", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setFilters"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setSearchTerm", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setSearchTerm"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setTimeRange", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setTimeRange"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setQueryReady", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setQueryReady"), _class.prototype), _class);